import { useState } from 'react';
import { Table } from '@tanstack/react-table';

import { FileUploadDTO } from '@/types/api-types';

import IconDeleteAll from '@/assets/svg/icon-delete-all.svg?react';
import IconDeleteSome from '@/assets/svg/icon-delete-some.svg?react';
import IconDeleteWithErrors from '@/assets/svg/icon-delete-with-errors.svg?react';

import { FILE_UPLOAD_STATUS } from '@/constants/global-constants';

import SelectionToolbar from '../FileExplorer/SelectionToolbar';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

interface UploadSelectionBarProps {
  table: Table<FileUploadDTO>;
  setFilesToUpload: (files: FileUploadDTO[]) => void;
}
const UploadSelectionBar = ({
  table,
  setFilesToUpload,
}: UploadSelectionBarProps) => {
  const [isDeleteSomeDialogOpen, setIsDeleteSomeDialogOpen] = useState(false);
  const [isDeleteWithErrorsDialogOpen, setIsDeleteWithErrorsDialogOpen] =
    useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);

  const handleDeleteSome = () => {
    const selectedRows = table.getSelectedRowModel().rows;
    const allRows = table.getRowModel().rows;
    const allRowsWithoutSelected = allRows.filter(
      (row) => !selectedRows.includes(row),
    );
    setFilesToUpload(allRowsWithoutSelected.map((row) => row.original));

    setIsDeleteSomeDialogOpen(false);
    // Clear selection after mutation to avoid stale selected state
    table.resetRowSelection();
  };

  const handleDeleteWithErrors = () => {
    const allRows = table.getRowModel().rows;
    const allRowsWithoutSelected = allRows.filter(
      (row) => row.original.status !== FILE_UPLOAD_STATUS.UPLOAD_FAILED,
    );
    setFilesToUpload(allRowsWithoutSelected.map((row) => row.original));

    setIsDeleteWithErrorsDialogOpen(false);
    // Clear selection after mutation
    table.resetRowSelection();
  };

  const handleDeleteAll = () => {
    setFilesToUpload([]);
    setDeleteAllDialogOpen(false);
    // Clear selection after mutation
    table.resetRowSelection();
  };

  return (
    <>
      <SelectionToolbar
        selectedCount={table.getSelectedRowModel().rows.length}
        // onClearSelection={() => table.resetRowSelection()}
        classNameActions="gap-10"
        actions={
          <>
            <button
              className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => setIsDeleteSomeDialogOpen(true)}
            >
              <IconDeleteSome className="h-3 w-3 min-w-3" />
              <span className="whitespace-nowrap font-medium">
                Видалити обрані
              </span>
            </button>
            <button
              className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white"
              onClick={() => setIsDeleteWithErrorsDialogOpen(true)}
            >
              <IconDeleteWithErrors className="h-4 w-4 min-w-4" />
              <span className="whitespace-nowrap font-medium">
                Видалити з помилками
              </span>
            </button>
            <button
              className="flex items-center gap-2 rounded-md p-2 transition-colors duration-300 hover:bg-white"
              onClick={() => setDeleteAllDialogOpen(true)}
            >
              <IconDeleteAll className="h-5 w-5 min-w-5" />
              <span className="whitespace-nowrap font-medium">
                Видалити всі
              </span>
            </button>
          </>
        }
      />
      <Dialog
        open={isDeleteSomeDialogOpen}
        onOpenChange={setIsDeleteSomeDialogOpen}
      >
        <DialogContent className="p-4">
          <DialogHeader className="flex flex-col items-center p-6 pb-0">
            <DialogTitle className="p-4 text-center text-2xl font-normal">
              Ви впевнені, що бажаєте видалити ці файли?
            </DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-2 px-4 pb-4 md:justify-center">
            <Button variant="destructive" onClick={handleDeleteSome}>
              Видалити
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsDeleteSomeDialogOpen(false)}
            >
              Скасувати
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isDeleteWithErrorsDialogOpen}
        onOpenChange={setIsDeleteWithErrorsDialogOpen}
      >
        <DialogContent className="p-4">
          <DialogHeader className="flex flex-col items-center p-6 pb-0">
            <DialogTitle className="p-3 text-center text-2xl font-normal text-primary">
              Ви впевнені, що бажаєте видалити всі файли з помилками?
            </DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>

          <DialogFooter className="flex justify-center gap-2 px-4 pb-4 md:justify-center">
            <Button variant="destructive" onClick={handleDeleteWithErrors}>
              Видалити
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsDeleteWithErrorsDialogOpen(false)}
            >
              Скасувати
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={deleteAllDialogOpen} onOpenChange={setDeleteAllDialogOpen}>
        <DialogContent className="p-4">
          <DialogHeader className="flex flex-col items-center p-6 pb-0">
            <DialogTitle className="p-4 text-center text-2xl font-normal text-primary">
              Ви впевнені, що бажаєте видалити всі файли?
            </DialogTitle>
            <DialogDescription></DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex justify-center gap-2 px-4 pb-4 md:justify-center">
            <Button variant="destructive" onClick={handleDeleteAll}>
              Видалити
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteAllDialogOpen(false)}
            >
              Скасувати
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UploadSelectionBar;
